/** 绩效分析（analytics 屏）的契约解析。
 *
 * - `GET /validation/backtest`：someo-backtrader 修正版回测，后端用 keel `compute_metrics` 重算（裁决 B7），
 *   回测仓自报的一族只作 `reported` 对照；回撤一律是正数幅度。前端不算年化 / 回撤 / Sharpe。
 * - `GET /attribution/report?strategy=`：四层取数对应表未定前一律「缺」（裁决 B8），缺的层不当 0。
 */

import { apiUrl, expectRecord, optionalNumber, optionalString, query, requireNumber, requireString } from "../api/client";
import { ContractError } from "../api/errors";

export interface Segment {
  period: [string, string];
  annualized_return: number;
  max_drawdown: number;
  sharpe: number;
  cumulative_return: number | null;
  trading_days: number;
  trades: number | null;
}

export interface Reported {
  annualized_return: number | null;
  max_drawdown: number | null;
  sharpe: number | null;
}

export interface CorrectedRun {
  source_digest: string | null;
  corrections: string[];
  full: Segment | null;
  sample_in: Segment | null;
  sample_out: Segment | null;
  cutoff: string | null;
  note: string | null;
  reported: { family: string | null; full: Reported | null; sample_in: Reported | null; sample_out: Reported | null };
}

export interface BacktestStrategy {
  backtest_id: string;
  strategy_id: string | null;
  corrected: CorrectedRun | null;
}

export interface BacktestPayload {
  source: { path: string; sha256: string; repo_commit: string | null; snapshot_id: string | null; period: { start: string; end: string } | null };
  formula: { engine: string; basis: string; risk_free_annual: number | null; sample_in_note: string | null };
  strategies: BacktestStrategy[];
}

const BT = () => apiUrl("/validation/backtest");

function segment(raw: unknown, what: string): Segment | null {
  if (raw === null || raw === undefined) return null;
  const url = BT();
  const s = expectRecord(raw, url, what);
  const period = s["period"];
  if (!Array.isArray(period) || period.length !== 2 || period.some((d) => typeof d !== "string")) {
    throw new ContractError(`${what}.period 应为 [起, 止]`, url, s);
  }
  return {
    period: [period[0] as string, period[1] as string],
    annualized_return: requireNumber(s, "annualized_return", url),
    max_drawdown: requireNumber(s, "max_drawdown", url),
    sharpe: requireNumber(s, "sharpe", url),
    cumulative_return: optionalNumber(s, "cumulative_return"),
    trading_days: requireNumber(s, "trading_days", url),
    trades: optionalNumber(s, "trades"),
  };
}

function reported(raw: unknown): Reported | null {
  if (raw === null || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return { annualized_return: optionalNumber(r, "annualized_return"), max_drawdown: optionalNumber(r, "max_drawdown"), sharpe: optionalNumber(r, "sharpe") };
}

export function parseBacktest(body: unknown): BacktestPayload {
  const url = BT();
  const b = expectRecord(body, url, "回测绩效");
  const source = expectRecord(b["source"], url, "source");
  const formula = expectRecord(b["formula"], url, "formula");
  const list = b["strategies"];
  if (!Array.isArray(list)) throw new ContractError("回测绩效缺少数组字段 `strategies`", url, b);
  const period = source["period"] && typeof source["period"] === "object" ? (source["period"] as Record<string, unknown>) : null;
  return {
    source: {
      path: requireString(source, "path", url),
      sha256: requireString(source, "sha256", url),
      repo_commit: optionalString(source, "repo_commit"),
      snapshot_id: optionalString(source, "snapshot_id"),
      period: period ? { start: requireString(period, "start", url), end: requireString(period, "end", url) } : null,
    },
    formula: {
      engine: requireString(formula, "engine", url),
      basis: requireString(formula, "basis", url),
      risk_free_annual: optionalNumber(formula, "risk_free_annual"),
      sample_in_note: optionalString(formula, "sample_in_note"),
    },
    strategies: list.map((item, i) => {
      const s = expectRecord(item, url, `strategies[${i}]`);
      const c = s["corrected"];
      let corrected: CorrectedRun | null = null;
      if (c !== null && c !== undefined) {
        const r = expectRecord(c, url, `strategies[${i}].corrected`);
        const rep = r["reported"] && typeof r["reported"] === "object" ? (r["reported"] as Record<string, unknown>) : {};
        corrected = {
          source_digest: optionalString(r, "source_digest"),
          corrections: Array.isArray(r["corrections"]) ? r["corrections"].map(String) : [],
          full: segment(r["full"], "full"),
          sample_in: segment(r["sample_in"], "sample_in"),
          sample_out: segment(r["sample_out"], "sample_out"),
          cutoff: optionalString(r, "cutoff"),
          note: optionalString(r, "note"),
          reported: { family: optionalString(rep, "family"), full: reported(rep["full"]), sample_in: reported(rep["sample_in"]), sample_out: reported(rep["sample_out"]) },
        };
      }
      return { backtest_id: requireString(s, "backtest_id", url), strategy_id: optionalString(s, "strategy_id"), corrected };
    }),
  };
}

export interface AttributionLayer {
  layer: string;
  contribution: number | null;
  status: string;
}

export interface AttributionReport {
  strategy_id: string;
  status: string;
  reason: string | null;
  total_diff: number | null;
  residual: number | null;
  main_source: string | null;
  layers: AttributionLayer[];
  candidate: { backtest_id: string; kind: string; items: number } | null;
}

export function parseAttributionReport(body: unknown, sid: string): AttributionReport {
  const url = apiUrl(`/attribution/report?strategy=${encodeURIComponent(sid)}`);
  const r = expectRecord(body, url, "归因报告");
  const layers = r["layers"];
  if (!Array.isArray(layers)) throw new ContractError("归因报告缺少数组字段 `layers`", url, r);
  const cand = r["candidate_source"];
  let candidate: AttributionReport["candidate"] = null;
  if (cand !== null && cand !== undefined) {
    const c = expectRecord(cand, url, "candidate_source");
    candidate = { backtest_id: requireString(c, "backtest_id", url), kind: requireString(c, "kind", url), items: requireNumber(c, "items", url) };
  }
  return {
    strategy_id: requireString(r, "strategy_id", url),
    status: requireString(r, "status", url),
    reason: optionalString(r, "reason"),
    total_diff: optionalNumber(r, "total_diff"),
    residual: optionalNumber(r, "residual"),
    main_source: optionalString(r, "main_source"),
    layers: layers.map((l, i) => {
      const x = expectRecord(l, url, `layers[${i}]`);
      return { layer: requireString(x, "layer", url), contribution: optionalNumber(x, "contribution"), status: requireString(x, "status", url) };
    }),
    candidate,
  };
}

export const readBacktest = () => query<unknown>("/validation/backtest").then(parseBacktest);
export const readAttributionReport = (sid: string) =>
  query<unknown>("/attribution/report", { strategy: sid }).then((body) => parseAttributionReport(body, sid));

/** 残差判读：有层为「缺」时残差不可算（显示「—」），否则非 0 即对不平。 */
export function residualVerdict(report: AttributionReport): "unknown" | "balanced" | "unbalanced" {
  if (report.layers.some((l) => l.status === "缺") || report.residual === null) return "unknown";
  return Math.abs(report.residual) < 1e-9 ? "balanced" : "unbalanced";
}
